import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Stack, Button,
  TextField, Alert, MenuItem, Switch, FormControlLabel,
  alpha, useTheme, LinearProgress, ToggleButtonGroup, ToggleButton, Divider,
} from '@mui/material';
import { Sparkles, PartyPopper, Languages } from 'lucide-react';
import axios from '../utils/axios';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';

// Festival languages. English maps to the top-level fields; hi/gu live in `translations`.
const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'gu', label: 'ગુજરાતી' },
];

const EFFECTS = [
  { value: 'fireworks', label: 'Fireworks 🎆' },
  { value: 'confetti', label: 'Confetti 🎊' },
  { value: 'snow', label: 'Snow ❄️' },
  { value: 'petals', label: 'Falling petals 🌸' },
  { value: 'sparkles', label: 'Sparkles ✨' },
  { value: 'float', label: 'Floating up 🪁 (kites / balloons)' },
  { value: 'none', label: 'No effect' },
];
const INTENSITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// Ready-made themes — selecting one prefills the themeable fields.
const PRESETS = {
  custom: { label: 'Custom' },
  diwali: {
    label: 'Diwali', effect: 'fireworks', emoji: '🪔',
    primary_color: '#FF6D00', secondary_color: '#FFD600', text_color: '#ffffff',
    greeting_title: 'Happy Diwali! 🪔',
    greeting_message: 'Wishing you light, joy and prosperity. Celebrate the festival of lights with us!',
    banner_text: '✨ Diwali Dhamaka — festive offers live now!',
  },
  holi: {
    label: 'Holi', effect: 'petals', emoji: '🎨',
    primary_color: '#E91E63', secondary_color: '#00BCD4', text_color: '#ffffff',
    greeting_title: 'Happy Holi! 🎨',
    greeting_message: 'May your life be as colourful as the festival of Holi. Play safe, shop happy!',
    banner_text: '🌈 Holi celebration is on — splash of festive deals!',
  },
  christmas: {
    label: 'Christmas', effect: 'snow', emoji: '🎄',
    primary_color: '#0F8A3C', secondary_color: '#D32F2F', text_color: '#ffffff',
    greeting_title: 'Merry Christmas! 🎄',
    greeting_message: 'Tis the season to celebrate. Warm wishes and festive surprises await!',
    banner_text: '🎅 Christmas cheer — special season offers!',
  },
  'new-year': {
    label: 'New Year', effect: 'fireworks', emoji: '🎆',
    primary_color: '#FE5F01', secondary_color: '#FFC107', text_color: '#ffffff',
    greeting_title: 'Happy New Year! 🎆',
    greeting_message: 'Here is to new beginnings. Start the year with great deals!',
    banner_text: '🥂 New Year, new offers — celebrate with us!',
  },
  'independence-day': {
    label: 'Independence Day', effect: 'confetti', emoji: '🇮🇳',
    primary_color: '#FF9933', secondary_color: '#138808', text_color: '#ffffff',
    greeting_title: 'Happy Independence Day! 🇮🇳',
    greeting_message: 'Celebrating the spirit of freedom. Jai Hind!',
    banner_text: '🇮🇳 Independence Day specials are live!',
  },
  eid: {
    label: 'Eid', effect: 'sparkles', emoji: '🌙',
    primary_color: '#1B5E20', secondary_color: '#FBC02D', text_color: '#ffffff',
    greeting_title: 'Eid Mubarak! 🌙',
    greeting_message: 'Wishing you peace, prosperity and festive joy this Eid.',
    banner_text: '🌙 Eid celebration — festive offers await!',
  },
  navratri: {
    label: 'Navratri', effect: 'confetti', emoji: '💃',
    primary_color: '#D32F2F', secondary_color: '#FFB300', text_color: '#ffffff',
    greeting_title: 'Happy Navratri! 💃',
    greeting_message: 'Nine nights of devotion, dance and colour. Celebrate Garba with festive deals!',
    banner_text: '🪕 Navratri celebration — festive offers live now!',
  },
  'makar-sankranti': {
    label: 'Makar Sankranti', effect: 'float', emoji: '🪁',
    primary_color: '#039BE5', secondary_color: '#FFD54F', text_color: '#ffffff',
    greeting_title: 'Happy Makar Sankranti! 🪁',
    greeting_message: 'Soar high like a kite this harvest season. Wishing you joy and prosperity!',
    banner_text: '🪁 Makar Sankranti — let the festive offers fly!',
  },
  dussehra: {
    label: 'Dussehra', effect: 'fireworks', emoji: '🏹',
    primary_color: '#E65100', secondary_color: '#FFC107', text_color: '#ffffff',
    greeting_title: 'Happy Dussehra! 🏹',
    greeting_message: 'Celebrating the victory of good over evil. May positivity win in your life!',
    banner_text: '🏹 Dussehra celebration — festive offers live now!',
  },
  janmashtami: {
    label: 'Janmashtami', effect: 'petals', emoji: '🪈',
    primary_color: '#1565C0', secondary_color: '#FFB300', text_color: '#ffffff',
    greeting_title: 'Happy Janmashtami! 🪈',
    greeting_message: 'Celebrating the birth of Lord Krishna. May your life be filled with love, joy and devotion.',
    banner_text: '🪈 Janmashtami celebration — festive offers live now!',
  },
  'ganesh-chaturthi': {
    label: 'Ganesh Chaturthi', effect: 'petals', emoji: '🐘',
    primary_color: '#C2185B', secondary_color: '#FFB300', text_color: '#ffffff',
    greeting_title: 'Ganpati Bappa Morya! 🐘',
    greeting_message: 'May Lord Ganesha bring wisdom, prosperity and good fortune your way.',
    banner_text: '🪔 Ganesh Chaturthi — festive blessings & offers!',
  },
  'raksha-bandhan': {
    label: 'Raksha Bandhan', effect: 'petals', emoji: '🎀',
    primary_color: '#C2185B', secondary_color: '#FFB300', text_color: '#ffffff',
    greeting_title: 'Happy Raksha Bandhan! 🎀',
    greeting_message: 'Celebrating the beautiful bond of love between brothers and sisters — tied with a rakhi.',
    banner_text: '🎀 Raksha Bandhan — special rakhi gifts & festive offers!',
  },
  'republic-day': {
    label: 'Republic Day', effect: 'confetti', emoji: '🇮🇳',
    primary_color: '#FF9933', secondary_color: '#138808', text_color: '#ffffff',
    greeting_title: 'Happy Republic Day! 🇮🇳',
    greeting_message: 'Honouring the spirit of our nation. Jai Hind!',
    banner_text: '🇮🇳 Republic Day specials are live!',
  },
  pongal: {
    label: 'Pongal', effect: 'confetti', emoji: '🌾',
    primary_color: '#FB8C00', secondary_color: '#43A047', text_color: '#ffffff',
    greeting_title: 'Happy Pongal! 🌾',
    greeting_message: 'Celebrating the harvest with gratitude and joy. Pongalo Pongal!',
    banner_text: '🌾 Pongal harvest festival — festive offers live!',
  },
  onam: {
    label: 'Onam', effect: 'petals', emoji: '🌸',
    primary_color: '#2E7D32', secondary_color: '#FBC02D', text_color: '#ffffff',
    greeting_title: 'Happy Onam! 🌸',
    greeting_message: 'Wishing you a colourful Onam filled with happiness and prosperity.',
    banner_text: '🌸 Onam celebration — festive deals await!',
  },
  lohri: {
    label: 'Lohri', effect: 'sparkles', emoji: '🔥',
    primary_color: '#E64A19', secondary_color: '#FDD835', text_color: '#ffffff',
    greeting_title: 'Happy Lohri! 🔥',
    greeting_message: 'Gather around the bonfire and celebrate the harvest with warmth and joy.',
    banner_text: '🔥 Lohri celebration — festive offers blazing now!',
  },
};

const EMPTY = {
  name: '', preset: 'custom', effect: 'confetti', emoji: '🎉', enabled: false,
  starts_at: '', ends_at: '',
  greeting_title: '', greeting_message: '', banner_text: '', cta_text: '', cta_link: '',
  translations: { hi: {}, gu: {} },
  primary_color: '#f75d34', secondary_color: '#FFC107', text_color: '#ffffff',
  show_welcome_popup: true, show_banner: true, background_effect: true, intensity: 'medium',
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const ColorField = ({ label, value, onChange }) => (
  <Box>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{label}</Typography>
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="input"
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width: 44, height: 40, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'transparent', cursor: 'pointer' }}
      />
      <TextField size="small" value={value} onChange={(e) => onChange(e.target.value)} sx={{ flex: 1 }} />
    </Stack>
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase', mb: 1.5 }}>
    {children}
  </Typography>
);

const FestivalModal = ({ open, onClose, festival, onSuccess }) => {
  const theme = useTheme();
  const isEdit = Boolean(festival?._id);
  const [form, setForm] = useState(EMPTY);
  const [contentLang, setContentLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setContentLang('en');
    if (festival) {
      setForm({
        ...EMPTY,
        ...festival,
        translations: {
          hi: { ...(festival.translations?.hi || {}) },
          gu: { ...(festival.translations?.gu || {}) },
        },
        starts_at: toLocalInput(festival.starts_at),
        ends_at: toLocalInput(festival.ends_at),
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, festival]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Read/write a localised copy field for the language tab currently selected.
  // English → top-level field; hi/gu → form.translations[lang].
  const tVal = (field) =>
    contentLang === 'en' ? (form[field] ?? '') : (form.translations?.[contentLang]?.[field] ?? '');
  const tSet = (field, value) => {
    if (contentLang === 'en') { set(field, value); return; }
    setForm((f) => ({
      ...f,
      translations: {
        ...f.translations,
        [contentLang]: { ...(f.translations?.[contentLang] || {}), [field]: value },
      },
    }));
  };

  const applyPreset = (preset) => {
    const p = PRESETS[preset];
    if (!p || preset === 'custom') { set('preset', preset); return; }
    setForm((f) => ({
      ...f,
      preset,
      effect: p.effect ?? f.effect,
      emoji: p.emoji ?? f.emoji,
      primary_color: p.primary_color ?? f.primary_color,
      secondary_color: p.secondary_color ?? f.secondary_color,
      text_color: p.text_color ?? f.text_color,
      greeting_title: p.greeting_title ?? f.greeting_title,
      greeting_message: p.greeting_message ?? f.greeting_message,
      banner_text: p.banner_text ?? f.banner_text,
      // Keep the admin's existing name if they typed one, else suggest the preset label.
      name: f.name?.trim() ? f.name : p.label,
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Festival name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, starts_at: form.starts_at || null, ends_at: form.ends_at || null };
      if (isEdit) await axios.put(`/admin/festivals/${festival._id}`, payload, { skipNotify: 'success' });
      else await axios.post('/admin/festivals', payload, { skipNotify: 'success' });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save festival');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={!saving ? onClose : undefined} maxWidth="md">
      <AdminDialogHeader
        title={isEdit ? 'Edit Festival' : 'New Festival'}
        subtitle="Configure the celebration shown to website visitors. Turn it on to go live."
        icon={Sparkles}
        onClose={!saving ? onClose : undefined}
      />
      {saving && <LinearProgress />}
      <AdminDialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* ── Settings column ── */}
          <Grid size={{ xs: 12, md: 7 }}>
            <SectionLabel>Basics</SectionLabel>
            <Stack spacing={2}>
              <TextField fullWidth label="Festival name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Diwali 2026" />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select fullWidth label="Preset theme" value={form.preset} onChange={(e) => applyPreset(e.target.value)}>
                    {Object.entries(PRESETS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    sx={{ ml: 0.5, mt: 0.5 }}
                    control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
                    label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>{form.enabled ? 'Live (On)' : 'Off'}</Typography>}
                  />
                </Grid>
              </Grid>
            </Stack>

            <Divider sx={{ my: 2.5 }} />
            <SectionLabel>Theme & Effect</SectionLabel>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth label="Effect" value={form.effect} onChange={(e) => set('effect', e.target.value)}>
                  {EFFECTS.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="Emoji" value={form.emoji} onChange={(e) => set('emoji', e.target.value)} inputProps={{ maxLength: 4 }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField select fullWidth label="Intensity" value={form.intensity} onChange={(e) => set('intensity', e.target.value)}>
                  {INTENSITIES.map((i) => <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}><ColorField label="Primary color" value={form.primary_color} onChange={(v) => set('primary_color', v)} /></Grid>
              <Grid size={{ xs: 12, sm: 4 }}><ColorField label="Secondary color" value={form.secondary_color} onChange={(v) => set('secondary_color', v)} /></Grid>
              <Grid size={{ xs: 12, sm: 4 }}><ColorField label="Text color" value={form.text_color} onChange={(v) => set('text_color', v)} /></Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
              <SectionLabel>Welcome message</SectionLabel>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={contentLang}
                onChange={(_e, v) => v && setContentLang(v)}
                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontWeight: 700, textTransform: 'none' } }}
              >
                {LANGS.map((l) => (
                  <ToggleButton key={l.code} value={l.code}>{l.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            {contentLang !== 'en' && (
              <Alert severity="info" icon={<Languages size={18} />} sx={{ mb: 1.5, py: 0.25, fontSize: 12.5 }}>
                Editing the <b>{LANGS.find((l) => l.code === contentLang)?.label}</b> version. Leave blank to fall back to English on the site.
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField fullWidth label="Greeting title" value={tVal('greeting_title')} onChange={(e) => tSet('greeting_title', e.target.value)} placeholder={contentLang === 'en' ? 'Happy Diwali! 🪔' : form.greeting_title || ''} />
              <TextField fullWidth multiline minRows={2} label="Greeting message" value={tVal('greeting_message')} onChange={(e) => tSet('greeting_message', e.target.value)} placeholder={contentLang === 'en' ? '' : form.greeting_message || ''} />
              <TextField fullWidth label="CTA button text (optional)" value={tVal('cta_text')} onChange={(e) => tSet('cta_text', e.target.value)} placeholder={contentLang === 'en' ? 'Shop festive deals' : form.cta_text || ''} />
              <TextField fullWidth label="Banner text (optional top strip)" value={tVal('banner_text')} onChange={(e) => tSet('banner_text', e.target.value)} placeholder={contentLang === 'en' ? '' : form.banner_text || ''} />
              <TextField fullWidth label="CTA link (optional, same for all languages)" value={form.cta_link} onChange={(e) => set('cta_link', e.target.value)} placeholder="/search?q=offers" />
            </Stack>

            <Divider sx={{ my: 2.5 }} />
            <SectionLabel>Schedule (optional)</SectionLabel>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth type="datetime-local" label="Starts at" slotProps={{ inputLabel: { shrink: true } }} InputLabelProps={{ shrink: true }} value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} onClick={(e) => e.target.showPicker?.()} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth type="datetime-local" label="Ends at" slotProps={{ inputLabel: { shrink: true } }} InputLabelProps={{ shrink: true }} value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} onClick={(e) => e.target.showPicker?.()} />
              </Grid>
            </Grid>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1 }}>
              Leave blank to control purely with the On/Off switch. With dates set, it goes live automatically within the window (while On).
            </Typography>

            <Divider sx={{ my: 2.5 }} />
            <SectionLabel>Display options</SectionLabel>
            <Stack>
              <FormControlLabel control={<Switch checked={form.show_welcome_popup} onChange={(e) => set('show_welcome_popup', e.target.checked)} />} label="Show welcome popup (once per visit)" />
              <FormControlLabel control={<Switch checked={form.show_banner} onChange={(e) => set('show_banner', e.target.checked)} />} label="Show top banner strip" />
              <FormControlLabel control={<Switch checked={form.background_effect} onChange={(e) => set('background_effect', e.target.checked)} />} label="Ongoing background effect" />
            </Stack>
          </Grid>

          {/* ── Live preview column ── */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <SectionLabel>Preview</SectionLabel>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {LANGS.find((l) => l.code === contentLang)?.label}
              </Typography>
            </Stack>
            {(() => {
              // Preview reflects the language tab, with English fallback (as on the site).
              const pTitle = tVal('greeting_title') || form.greeting_title;
              const pMsg = tVal('greeting_message') || form.greeting_message;
              const pCta = tVal('cta_text') || form.cta_text;
              const pBanner = tVal('banner_text') || form.banner_text;
              return (
            <Box sx={{ position: 'sticky', top: 8 }}>
              {/* Banner preview */}
              {form.show_banner && pBanner && (
                <Box sx={{
                  borderRadius: 2, px: 2, py: 1, mb: 1.5, textAlign: 'center', fontWeight: 700, fontSize: 13,
                  background: `linear-gradient(90deg, ${form.primary_color}, ${form.secondary_color})`,
                  color: form.text_color,
                }}>
                  {form.emoji} {pBanner}
                </Box>
              )}
              {/* Popup card preview */}
              <Box sx={{
                borderRadius: 3, p: 3, textAlign: 'center', position: 'relative', overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(160deg, ${alpha(form.primary_color, 0.16)}, ${alpha(form.secondary_color, 0.10)})`,
              }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 1.5, fontSize: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`,
                }}>
                  {form.emoji}
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 0.5 }}>
                  {pTitle || 'Greeting title'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: pCta ? 2 : 0 }}>
                  {pMsg || 'Your welcome message preview appears here.'}
                </Typography>
                {pCta && (
                  <Box sx={{
                    display: 'inline-block', px: 2.5, py: 1, borderRadius: 2, fontWeight: 700, fontSize: 13,
                    background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`,
                    color: form.text_color,
                  }}>
                    {pCta}
                  </Box>
                )}
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 1.5, color: 'text.disabled' }}>
                <Sparkles size={13} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                  Effect: {EFFECTS.find((e) => e.value === form.effect)?.label || form.effect} · {form.intensity}
                </Typography>
              </Stack>
            </Box>
              );
            })()}
          </Grid>
        </Grid>
      </AdminDialogContent>

      <AdminDialogActions>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <PartyPopper size={16} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Turn “On” to celebrate · only the latest live festival shows on the site</Typography>
        </Box>
        <Button onClick={onClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>
        <Button onClick={save} variant="contained" color="primary" disabled={saving} sx={{ px: 3, fontWeight: 700 }}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Festival'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

export default FestivalModal;
